/**
 * HabitCalendar — monthly grid showing workout/food/rest day status.
 * Embedded in Health dashboard between Heatmap and Workout History.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

export default function HabitCalendar() {
  const [date, setDate] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [popover, setPopover] = useState(null);

  const m = date.getMonth() + 1;
  const y = date.getFullYear();

  const load = () => {
    apiClient
      .get(`/habits/calendar?month=${m}&year=${y}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  };
  useEffect(() => { load(); }, [m, y]);

  const grid = useMemo(() => {
    const firstDay = new Date(y, m - 1, 1).getDay(); // 0 = Sunday
    const offsetMon = (firstDay + 6) % 7; // 0 = Monday
    const daysInMonth = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < offsetMon; i++) cells.push(null);
    const dayMap = {};
    (data?.days || []).forEach((d) => { dayMap[d.date] = d; });
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const day = dayMap[iso] || { date: iso, workout: false, food: false, rest: false };
      cells.push({ ...day, dayNum: d });
    }
    return cells;
  }, [m, y, data]);

  const todayIso = new Date().toISOString().slice(0, 10);

  const toggleRest = async (cell) => {
    try {
      const { data: r } = await apiClient.post("/habits/rest-day", {
        date: cell.date,
        rest: !cell.rest,
      });
      toast.success(r.rest ? "Marked as rest day" : "Rest day removed");
      setPopover(null);
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const monthLabel = date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const summary = data?.summary || { active: 0, rest: 0, missed: 0 };

  return (
    <section data-testid="habit-calendar">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-[20px]">Habits</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDate(new Date(y, m - 2, 1))}
            data-testid="habits-prev"
            aria-label="Previous month"
            className="w-8 h-8 grid place-items-center rounded-full hover:bg-white"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="font-medium text-[13px]" data-testid="habits-month">{monthLabel}</div>
          <button
            onClick={() => setDate(new Date(y, m, 1))}
            data-testid="habits-next"
            aria-label="Next month"
            className="w-8 h-8 grid place-items-center rounded-full hover:bg-white"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div className="bg-white rounded-[16px] p-4 border border-line2">
        <div className="grid grid-cols-7 gap-1.5 text-center text-[10px] uppercase text-ink-tertiary mb-2">
          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
            <div key={i} className="font-medium tracking-wider">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {grid.map((cell, i) => {
            if (!cell) return <div key={i} />;
            const isToday = cell.date === todayIso;
            const fully = cell.workout && cell.food;
            const partial = (cell.workout || cell.food) && !fully;
            const rest = cell.rest && !fully && !partial;

            let bg = "transparent";
            let color = "var(--text-tertiary)";
            let border = "transparent";
            if (fully) {
              bg = "#00D4FF";
              color = "#000";
            } else if (partial) {
              bg = "rgba(0,212,255,0.18)";
              color = "var(--text-primary)";
              border = "#00D4FF";
            } else if (rest) {
              bg = "#ECEAE7";
              color = "var(--text-tertiary)";
            }

            return (
              <button
                key={i}
                onTouchStart={() => {
                  let timer = setTimeout(() => setPopover(cell), 500);
                  const cancel = () => clearTimeout(timer);
                  document.addEventListener("touchend", cancel, { once: true });
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setPopover(cell);
                }}
                data-testid={`habit-day-${cell.date}`}
                className="aspect-square grid place-items-center text-[12px] rounded-[8px]"
                style={{
                  background: bg,
                  color,
                  border: border !== "transparent" ? `1px solid ${border}` : "none",
                  fontWeight: isToday ? 800 : 500,
                  borderBottom: isToday ? "2px solid #00D4FF" : undefined,
                }}
              >
                {rest ? "R" : cell.dayNum}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          <Stat label="Active" value={summary.active} testid="habits-active" />
          <Stat label="Rest" value={summary.rest} testid="habits-rest" />
          <Stat label="Missed" value={summary.missed} testid="habits-missed" />
        </div>
      </div>

      <AnimatePresence>
        {popover && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPopover(null)}
            data-testid="habit-popover"
            className="fixed inset-0 z-[60] grid place-items-center bg-black/40 px-6"
          >
            <div className="bg-white rounded-[16px] p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-display text-[20px]">{popover.date}</h3>
              <div className="text-[13px] text-ink-soft mt-1">
                {popover.workout && "Workout completed · "}
                {popover.food && "Food logged · "}
                {!popover.workout && !popover.food && !popover.rest && "No activity"}
              </div>
              <div className="text-[12px] text-ink-soft mt-1">
                {popover.calories || 0} kcal · {popover.fitpoints || 0} FitPoints
              </div>
              <button
                onClick={() => toggleRest(popover)}
                data-testid="popover-rest-toggle"
                className="mt-3 w-full h-11 rounded-pill text-[13px] font-medium border border-line2"
                style={{
                  background: popover.rest ? "#ECEAE7" : "transparent",
                  borderRadius: 100,
                }}
              >
                {popover.rest ? "Remove rest day" : "Mark as rest day"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="text-center">
      <div className="font-barlow-cond text-[20px] font-bold leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-ink-tertiary mt-1">{label}</div>
    </div>
  );
}
